import React, { useMemo } from 'react';
import { Group, Circle, Text } from 'react-konva';
import { CELL_SIZE } from './GridLayer';
import { useCampaign } from '../../contexts/CampaignContext';
import { useAuth } from '../../contexts/AuthContext';

// Renders trap markers on the map. Each trap is visible if:
//   - the viewer is the DM, OR
//   - trap.triggered === true (globally revealed), OR
//   - trap.discoveredBy[viewerId] is set (this player discovered it).
//
// `bare` follows the same fragment convention as the other static-stack
// layers (shipped in iOS-perf Tier 1) so we share one Konva canvas.

export default function TrapLayer({ isDm = false, bare = false }) {
  const { traps } = useCampaign();
  const { auth }  = useAuth();
  const viewerPlayerId = auth?.playerId || null;

  const visibleTraps = useMemo(() => {
    return Object.values(traps || {}).filter((t) => {
      if (isDm) return true;
      if (t.triggered) return true;
      if (viewerPlayerId && t.discoveredBy?.[viewerPlayerId]) return true;
      return false;
    });
  }, [traps, isDm, viewerPlayerId]);

  if (visibleTraps.length === 0) return bare ? null : null;

  const body = (
    <>
      {visibleTraps.map((t) => {
        const cx = (t.x + 0.5) * CELL_SIZE;
        const cy = (t.y + 0.5) * CELL_SIZE;
        const fired   = !!t.triggered;
        const dmOnly  = isDm && !fired && !(viewerPlayerId && t.discoveredBy?.[viewerPlayerId]);
        const stroke  = fired ? '#c0392b' : (dmOnly ? '#b87333' : '#facc15');
        const dash    = dmOnly ? [4, 4] : null;
        // No shadowBlur — that filter recomputes a Gaussian blur every Stage
        // repaint, and after a trap reveals into the shared static Layer it
        // gets repainted on every Firestore update. Visually we lose a touch
        // of "pop" but the canvas stays smooth.
        return (
          <Group key={t.id} x={cx} y={cy} listening={false}>
            <Circle
              radius={CELL_SIZE * 0.32}
              stroke={stroke}
              strokeWidth={2.5}
              dash={dash}
              fill="rgba(40,15,15,0.55)"
              perfectDrawEnabled={false}
            />
            <Text
              text="🪤"
              fontSize={CELL_SIZE * 0.42}
              align="center"
              verticalAlign="middle"
              offsetX={CELL_SIZE * 0.21}
              offsetY={CELL_SIZE * 0.24}
              listening={false}
              perfectDrawEnabled={false}
            />
            {fired && (
              <Text
                text="!"
                fontSize={CELL_SIZE * 0.3}
                fontStyle="bold"
                fill="#fff"
                align="center"
                offsetX={3}
                offsetY={-CELL_SIZE * 0.4}
                listening={false}
                perfectDrawEnabled={false}
              />
            )}
          </Group>
        );
      })}
    </>
  );

  return bare ? body : <>{body}</>;
}
